import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { api, formatApiError, londonDateTime } from "@/lib/api";
import { inputClass } from "@/lib/constants";

const SECTION_META = {
  home: { label: "Homepage", fields: { hero_title: "Main heading", hero_subtitle: "Subheading", intro_heading: "Intro heading", intro_text: "Intro text", ordering_note: "Ordering note" } },
  about: { label: "About page", fields: { heading: "Heading", body: "Main text", secondary_body: "Second paragraph" } },
  events: { label: "Events & grazing", fields: { heading: "Heading", body: "Main text", enquiry_note: "Enquiry note" } },
  delivery_info: { label: "Delivery & collection info", fields: { heading: "Heading", collection_text: "Collection text", delivery_text: "Delivery text", cutoff_text: "Cut-off text" } },
  footer: { label: "Footer", fields: { tagline: "Tagline", smallprint: "Small print" } },
  newsletter: { label: "Newsletter box", fields: { heading: "Heading", description: "Description", button_label: "Button label" } },
};

const LEGAL_LABELS = { terms: "Terms & Conditions of Sale", refunds: "Cancellation & Refund Policy", delivery: "Delivery & Collection Policy", allergens: "Allergen & Food Information Policy", privacy: "Privacy Notice", cookies: "Cookie Policy" };

export default function AdminContent() {
  const [tab, setTab] = useState("home");
  const [content, setContent] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [legal, setLegal] = useState([]);
  const [legalDraft, setLegalDraft] = useState(null);
  const [legalPreview, setLegalPreview] = useState(null);

  const load = () => {
    api.get("/admin/content").then((r) => {
      setContent(r.data);
      const d = {};
      for (const key of Object.keys(r.data.sections)) {
        d[key] = { ...(r.data.draft?.[key] || r.data.published?.[key] || {}) };
      }
      setDrafts(d);
    }).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/content/faqs").then((r) => setFaqs(r.data)).catch(() => {});
    api.get("/admin/content/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/admin/content/legal").then((r) => setLegal(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveDraft = (key) => {
    api.put(`/admin/content/section/${key}`, { fields: drafts[key], base_updated_at: content?.updated_at })
      .then(() => { toast.success("Draft saved — preview it, then Publish when ready."); load(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const publish = (key) => {
    api.post(`/admin/content/publish/${key}`)
      .then(() => { toast.success("Published — live on the website now."); load(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const saveFaq = (faq) => {
    const fn = faq.id ? api.put(`/admin/content/faqs/${faq.id}`, faq) : api.post("/admin/content/faqs", faq);
    fn.then(() => { toast.success("FAQ saved."); load(); }).catch((e) => toast.error(formatApiError(e)));
  };

  const saveTestimonial = (t) => {
    const fn = t.id ? api.put(`/admin/content/testimonials/${t.id}`, t) : api.post("/admin/content/testimonials", t);
    fn.then(() => { toast.success("Testimonial saved."); load(); }).catch((e) => toast.error(formatApiError(e)));
  };

  const startLegalEdit = (kind) => {
    const existing = legal.find((l) => l.kind === kind);
    setLegalDraft({ kind, sections: (existing?.draft || []).map((s) => ({ ...s })) });
  };

  const previewLegal = (kind) => {
    api.get(`/admin/content/legal/${kind}/preview`)
      .then((r) => setLegalPreview(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };

  const saveLegal = () => {
    api.put(`/admin/content/legal/${legalDraft.kind}`, { sections: legalDraft.sections })
      .then(() => { toast.success("Draft saved — the public page is unchanged until you Publish."); setLegalDraft(null); load(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const legalAction = (kind, action, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    api.post(`/admin/content/legal/${kind}/${action}`, action === "reviewed" ? { confirm: true } : null)
      .then(() => { toast.success("Done."); load(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const tabs = [...Object.entries(SECTION_META).map(([k, v]) => [k, v.label]), ["faqs", "FAQs"], ["testimonials", "Testimonials"], ["legal", "Legal pages"]];

  return (
    <div data-testid="admin-content-page">
      <h1 className="font-serif text-2xl font-bold text-cream-text">Website Content</h1>
      <p className="mt-1 text-sm text-cream-muted">Edit the words on your website. Save a draft first, then Publish to make it live. Plain text only — the design stays intact.</p>

      <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Content sections">
        {tabs.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
            className={`min-h-[44px] rounded-full px-4 py-2 text-sm font-bold ${tab === key ? "bg-gold text-choco" : "border border-choco-line text-cream-muted hover:text-gold"}`}
            data-testid={`content-tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {SECTION_META[tab] && content && (
        <div className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid={`content-editor-${tab}`}>
          {Object.entries(SECTION_META[tab].fields).map(([field, label]) => (
            <div key={field} className="mt-4 first:mt-0">
              <label htmlFor={`ct-${tab}-${field}`} className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">{label}</label>
              <textarea id={`ct-${tab}-${field}`} rows={field.includes("body") || field.includes("text") ? 4 : 2}
                value={drafts[tab]?.[field] || ""}
                onChange={(e) => setDrafts({ ...drafts, [tab]: { ...drafts[tab], [field]: e.target.value } })}
                className={`${inputClass} mt-1 w-full`} data-testid={`content-field-${tab}-${field}`} />
            </div>
          ))}
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={() => saveDraft(tab)} className="inline-flex min-h-[48px] items-center gap-2 rounded-full border border-choco-line px-5 py-2.5 text-sm font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`content-save-${tab}`}>
              <Save className="h-4 w-4" aria-hidden="true" /> Save draft
            </button>
            <button onClick={() => publish(tab)} className="inline-flex min-h-[48px] items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco" data-testid={`content-publish-${tab}`}>
              <Upload className="h-4 w-4" aria-hidden="true" /> Publish live
            </button>
            <a href={tab === "home" ? "/" : tab === "delivery_info" ? "/delivery-collection" : `/${tab === "newsletter" || tab === "footer" ? "" : tab}`} target="_blank" rel="noreferrer"
              className="inline-flex min-h-[48px] items-center rounded-full border border-choco-line px-5 py-2.5 text-sm font-bold text-cream-muted hover:text-gold" data-testid={`content-view-${tab}`}>
              View page
            </a>
          </div>
        </div>
      )}

      {tab === "faqs" && (
        <div className="mt-6 space-y-4" data-testid="faq-editor">
          {faqs.map((f) => (
            <div key={f.id} className="rounded-3xl border border-choco-line bg-choco-light p-5" data-testid={`faq-row-${f.id}`}>
              <label htmlFor={`faq-q-${f.id}`} className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Question</label>
              <input id={`faq-q-${f.id}`} value={f.question} onChange={(e) => setFaqs(faqs.map((x) => x.id === f.id ? { ...x, question: e.target.value } : x))} className={`${inputClass} mt-1 w-full`} data-testid={`faq-question-${f.id}`} />
              <label htmlFor={`faq-a-${f.id}`} className="mt-3 block text-[11px] font-bold uppercase tracking-widest text-cream-muted">Answer</label>
              <textarea id={`faq-a-${f.id}`} rows={3} value={f.answer} onChange={(e) => setFaqs(faqs.map((x) => x.id === f.id ? { ...x, answer: e.target.value } : x))} className={`${inputClass} mt-1 w-full`} data-testid={`faq-answer-${f.id}`} />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label className="flex min-h-[44px] items-center gap-2 text-xs font-semibold text-cream-text">
                  <input type="checkbox" className="h-5 w-5" checked={f.active} onChange={(e) => setFaqs(faqs.map((x) => x.id === f.id ? { ...x, active: e.target.checked } : x))} data-testid={`faq-active-${f.id}`} /> Visible
                </label>
                <button onClick={() => saveFaq(f)} className="rounded-full bg-gold px-4 py-2 text-xs font-bold text-choco" data-testid={`faq-save-${f.id}`}>Save</button>
                <button onClick={() => { if (window.confirm("Delete this FAQ?")) api.delete(`/admin/content/faqs/${f.id}`).then(load).catch((e) => toast.error(formatApiError(e))); }}
                  className="rounded-full border border-red-500/40 px-4 py-2 text-xs font-bold text-red-300" data-testid={`faq-delete-${f.id}`}><Trash2 className="inline h-3.5 w-3.5" aria-hidden="true" /> Delete</button>
              </div>
            </div>
          ))}
          <button onClick={() => saveFaq({ question: "New question?", answer: "Answer…", sort_order: faqs.length, active: true })}
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full border border-choco-line px-5 py-2.5 text-sm font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="faq-add">
            <Plus className="h-4 w-4" aria-hidden="true" /> Add FAQ
          </button>
        </div>
      )}

      {tab === "testimonials" && (
        <div className="mt-6 space-y-4" data-testid="testimonial-editor">
          <p className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-xs text-amber-200">Only genuine customer words — never invented reviews.</p>
          {testimonials.map((t) => (
            <div key={t.id} className="rounded-3xl border border-choco-line bg-choco-light p-5" data-testid={`testimonial-row-${t.id}`}>
              <label htmlFor={`t-n-${t.id}`} className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Customer name</label>
              <input id={`t-n-${t.id}`} value={t.name} onChange={(e) => setTestimonials(testimonials.map((x) => x.id === t.id ? { ...x, name: e.target.value } : x))} className={`${inputClass} mt-1 w-full`} data-testid={`testimonial-name-${t.id}`} />
              <label htmlFor={`t-t-${t.id}`} className="mt-3 block text-[11px] font-bold uppercase tracking-widest text-cream-muted">Their words</label>
              <textarea id={`t-t-${t.id}`} rows={3} value={t.text} onChange={(e) => setTestimonials(testimonials.map((x) => x.id === t.id ? { ...x, text: e.target.value } : x))} className={`${inputClass} mt-1 w-full`} data-testid={`testimonial-text-${t.id}`} />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label className="flex min-h-[44px] items-center gap-2 text-xs font-semibold text-cream-text">
                  <input type="checkbox" className="h-5 w-5" checked={t.active} onChange={(e) => setTestimonials(testimonials.map((x) => x.id === t.id ? { ...x, active: e.target.checked } : x))} data-testid={`testimonial-active-${t.id}`} /> Visible
                </label>
                <button onClick={() => saveTestimonial(t)} className="rounded-full bg-gold px-4 py-2 text-xs font-bold text-choco" data-testid={`testimonial-save-${t.id}`}>Save</button>
                <button onClick={() => { if (window.confirm("Delete this testimonial?")) api.delete(`/admin/content/testimonials/${t.id}`).then(load).catch((e) => toast.error(formatApiError(e))); }}
                  className="rounded-full border border-red-500/40 px-4 py-2 text-xs font-bold text-red-300" data-testid={`testimonial-delete-${t.id}`}><Trash2 className="inline h-3.5 w-3.5" aria-hidden="true" /> Delete</button>
              </div>
            </div>
          ))}
          <button onClick={() => saveTestimonial({ name: "Customer name", text: "Their genuine words…", sort_order: testimonials.length, active: false })}
            className="inline-flex min-h-[48px] items-center gap-2 rounded-full border border-choco-line px-5 py-2.5 text-sm font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="testimonial-add">
            <Plus className="h-4 w-4" aria-hidden="true" /> Add testimonial
          </button>
        </div>
      )}

      {tab === "legal" && !legalDraft && (
        <div className="mt-6 space-y-4" data-testid="legal-list">
          <p className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-xs text-amber-200" data-testid="legal-admin-notice">
            <strong>Owner action needed:</strong> these documents are working drafts, not legal advice.
            Read each one carefully, get professional advice where appropriate, then Publish.
            Business details (name, address, email, phone, website) are filled in automatically from Business Settings.
            A page only appears on the public website once published.
          </p>
          {legal.map((doc) => {
            const dirty = JSON.stringify(doc.draft) !== JSON.stringify(doc.published);
            return (
              <div key={doc.kind} className="rounded-3xl border border-choco-line bg-choco-light p-5" data-testid={`legal-row-${doc.kind}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-cream-text">{doc.title || LEGAL_LABELS[doc.kind]}</p>
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-cream-muted">
                      {doc.is_published ? (
                        <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-bold text-green-400" data-testid={`legal-status-${doc.kind}`}>
                          Published{doc.published_at ? ` · ${londonDateTime(doc.published_at)}` : ""}
                        </span>
                      ) : (
                        <span className="rounded-full bg-cream-muted/15 px-2 py-0.5 text-[10px] font-bold text-cream-muted" data-testid={`legal-status-${doc.kind}`}>Not published — hidden from the public site</span>
                      )}
                      {doc.owner_reviewed ? (
                        <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-bold text-green-400" data-testid={`legal-reviewed-${doc.kind}`}>Owner reviewed{doc.reviewed_by ? ` by ${doc.reviewed_by}` : ""}</span>
                      ) : (
                        <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-300" data-testid={`legal-pending-${doc.kind}`}>Awaiting owner review</span>
                      )}
                      {dirty && doc.is_published && <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-bold text-blue-300">Draft differs from published</span>}
                      <span>Edited {londonDateTime(doc.updated_at)}</span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => startLegalEdit(doc.kind)} className="min-h-[44px] rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`legal-edit-${doc.kind}`}>Edit draft</button>
                    <button onClick={() => previewLegal(doc.kind)} className="min-h-[44px] rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`legal-preview-${doc.kind}`}>Preview</button>
                    {!doc.is_published && (
                      <button onClick={() => legalAction(doc.kind, "publish", `Publish ${doc.title} to the live website?`)} className="min-h-[44px] rounded-full bg-gold px-4 py-2 text-xs font-bold text-choco" data-testid={`legal-publish-${doc.kind}`}>Publish</button>
                    )}
                    {doc.is_published && dirty && (
                      <button onClick={() => legalAction(doc.kind, "publish", "Publish the draft over the current live version?")} className="min-h-[44px] rounded-full bg-gold px-4 py-2 text-xs font-bold text-choco" data-testid={`legal-publish-${doc.kind}`}>Publish changes</button>
                    )}
                    {doc.is_published && (
                      <button onClick={() => legalAction(doc.kind, "unpublish", `Unpublish ${doc.title}? The page will disappear from the public site.`)} className="min-h-[44px] rounded-full border border-red-500/40 px-4 py-2 text-xs font-bold text-red-300" data-testid={`legal-unpublish-${doc.kind}`}>Unpublish</button>
                    )}
                    {doc.is_published && dirty && (
                      <button onClick={() => legalAction(doc.kind, "discard", "Abandon the draft edits and restore the currently published version?")} className="min-h-[44px] rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-muted hover:text-gold" data-testid={`legal-discard-${doc.kind}`}>Restore published</button>
                    )}
                    {!doc.owner_reviewed && (
                      <button onClick={() => legalAction(doc.kind, "reviewed", `Confirm you have personally read and approved the ${doc.title} wording?`)} className="min-h-[44px] rounded-full border border-green-500/40 px-4 py-2 text-xs font-bold text-green-300" data-testid={`legal-review-${doc.kind}`}>Mark reviewed</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "legal" && legalDraft && (
        <div className="mt-6 rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="legal-editor">
          <h2 className="font-serif text-lg font-semibold text-cream-text">{LEGAL_LABELS[legalDraft.kind]}</h2>
          {legalDraft.sections.map((s, i) => (
            <div key={i} className="mt-4 rounded-2xl border border-choco-line p-4">
              <label htmlFor={`legal-h-${i}`} className="text-[11px] font-bold uppercase tracking-widest text-cream-muted">Section heading</label>
              <input id={`legal-h-${i}`} value={s.heading} onChange={(e) => setLegalDraft({ ...legalDraft, sections: legalDraft.sections.map((x, xi) => xi === i ? { ...x, heading: e.target.value } : x) })} className={`${inputClass} mt-1 w-full`} data-testid={`legal-heading-${i}`} />
              <label htmlFor={`legal-b-${i}`} className="mt-3 block text-[11px] font-bold uppercase tracking-widest text-cream-muted">Section text</label>
              <textarea id={`legal-b-${i}`} rows={4} value={s.body} onChange={(e) => setLegalDraft({ ...legalDraft, sections: legalDraft.sections.map((x, xi) => xi === i ? { ...x, body: e.target.value } : x) })} className={`${inputClass} mt-1 w-full`} data-testid={`legal-body-${i}`} />
            </div>
          ))}
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={() => setLegalDraft({ ...legalDraft, sections: [...legalDraft.sections, { heading: "", body: "" }] })} className="rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text" data-testid="legal-add-section">Add section</button>
            <button onClick={saveLegal} className="rounded-full bg-gold px-5 py-2 text-sm font-bold text-choco" data-testid="legal-save">Save draft</button>
            <button onClick={() => previewLegal(legalDraft.kind)} className="rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="legal-preview-draft">Preview with business details</button>
            <button onClick={() => setLegalDraft(null)} className="rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-muted" data-testid="legal-cancel">Cancel</button>
          </div>
        </div>
      )}

      {legalPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-choco/80 p-4" role="dialog" aria-modal="true" aria-label="Legal page preview" data-testid="legal-preview-modal">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-choco-line bg-choco-light p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-serif text-lg font-semibold text-cream-text">{legalPreview.title}</h2>
                <p className="text-xs text-cream-muted">Draft preview with your current business details filled in.</p>
              </div>
              <button onClick={() => setLegalPreview(null)} className="rounded-full border border-choco-line px-3 py-1.5 text-xs font-bold text-cream-muted hover:text-gold" data-testid="legal-preview-close">Close</button>
            </div>
            <div className="mt-4 space-y-5">
              {legalPreview.sections.map((s, i) => (
                <section key={i}>
                  <h3 className="font-serif text-base font-semibold text-cream-text">{s.heading}</h3>
                  <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-cream-muted">{s.body}</p>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
